import { Component, OnInit } from '@angular/core';
import { DateTime } from 'luxon';
import { BidRoundStatus, BidRoundType, ReportFilterOptions } from '../../../interfaces/enums';
import { BidRoundService } from '../../../services/bid-round.service';
import { Router } from '@angular/router';

const DEFAULT_RESPONSE_ITEM_VALUE = '0';
const DEFAULT_BAR_WIDTH = 20;

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  // Top of dashboard fields
  status?: BidRoundStatus;
  bidStartAllocation?: string;
  bidEndAllocation?: string;

  // Middle of dashboard fields
  buyerCount = 0;
  sellerCount = 0;
  strikePrice: string | null = null;
  acceptedBidsCount = 0;
  rejectedBidsCount = 0;

  // Bottom of dashboard fields
  totalVolumeTraded: string = DEFAULT_RESPONSE_ITEM_VALUE;
  averagePrice: string = DEFAULT_RESPONSE_ITEM_VALUE;
  totalEnergyTraded: string = DEFAULT_RESPONSE_ITEM_VALUE;
  untradedEnergyVolume: string = DEFAULT_RESPONSE_ITEM_VALUE;

  // Other variables
  bidCategories: BidRoundType[] = Object.values(BidRoundType); // List of bid categories
  displayDate = DateTime.local();
  today = DateTime.local();
  currentBidCategory = this.bidCategories[0];
  defaultBidCategory = this.bidCategories[0];
  participantsCount = 0;
  readonly bidRoundStatus = BidRoundStatus;
  readonly reportFilterOptions = ReportFilterOptions;

  constructor(
    private bidRoundService: BidRoundService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.displayDate = DateTime.local();
    this.getBidRoundInformation();
  }

  getBidRoundInformation(): void {
    console.log("Current Bid Category: ", this.currentBidCategory)
    const unit = this.isDayAhead() ? 'days' : 'months';
    console.log("today: ", this.today)
    console.log("displayDate: ", this.displayDate)
    const difference = this.calculateTimeDifference(unit);
    console.log("Difference: ", difference)
    this.bidRoundService.getBidRoundInformation(this.currentBidCategory, difference)
      .subscribe(response => {
        this.strikePrice = response.strike_price;
        this.buyerCount = response.total_buyers;
        this.sellerCount = response.total_sellers;
        this.participantsCount = response.total_participants;
        this.acceptedBidsCount = response.total_accepted;
        this.rejectedBidsCount = response.total_rejected;
        this.averagePrice = response.average_price;
        this.totalVolumeTraded = response.volume_traded;
        this.totalEnergyTraded = response.total_kwh_traded;
        this.untradedEnergyVolume = response.untraded_units;
        this.status = response.status;
        this.bidStartAllocation = this.getResponseTime(response.end_time);
        console.log(response)
        console.log(this.status)
      });
  }

  calculateBarPercentage(value1: string, value2: string): string {
    if (value1 === DEFAULT_RESPONSE_ITEM_VALUE) return DEFAULT_BAR_WIDTH + '%';
    const num1 = parseFloat(value1);
    if (isNaN(num1)) return DEFAULT_BAR_WIDTH + '%';
    const num2 = parseFloat(value2);
    if (isNaN(num2)) return DEFAULT_BAR_WIDTH + '%';
    const result = (num1 / (num1 + num2)) * 100;
    if (result < DEFAULT_BAR_WIDTH) return DEFAULT_BAR_WIDTH + '%';
    return result + '%';
  }

  calculateBarPercentageAveragePrice(): string {
    const num = parseFloat(this.averagePrice);
    console.log("Average Price: ", num)
    if (!num || this.strikePrice === null) return `${DEFAULT_BAR_WIDTH}%`;
    const strikePrice = parseFloat(this.strikePrice);
    console.log("Strike Price: ", strikePrice)
    if (num < strikePrice) return `40%`;
    if (num === strikePrice) return `50%`;
    return `60%`;
  }

  calculateBarPercentageSingle(value1: string, returnValue: number): string {
    const num = parseFloat(value1);
    if (!num) return `${DEFAULT_BAR_WIDTH}%`;
    return `${returnValue}%`;
  }

  getResponseTime(date: string): string {
    const time = date.split(' ');
    return this.isDayAhead() ? time[1] + (time[2] ? time[2] : '') : date;
  }

  isDayAhead(): boolean {
    return this.currentBidCategory === BidRoundType.DayAhead;
  }

  offsetIncrease(): void {
    if (this.isDayAhead()) {
      this.displayDate = this.displayDate.plus({ days: 1 });
      console.log(this.calculateTimeDifference('days'))
    } else {
      this.displayDate = this.displayDate.plus({ months: 1 });
      console.log(this.calculateTimeDifference('months'))
    }
    this.getBidRoundInformation();
  }

  offsetDecrease(): void {
    if (this.isDayAhead()) {
      this.displayDate = this.displayDate.minus({ days: 1 });
      console.log(this.calculateTimeDifference('days'))
    } else {
      this.displayDate = this.displayDate.minus({ months: 1 });
      console.log(this.calculateTimeDifference('months'))
    }
    this.getBidRoundInformation();
  }

  toTime(): string {
    // This method gets called periodically for some reason but this works to update the time
    this.today = DateTime.local();
    return this.today.setLocale('en-US').toFormat('hh-mm a');
  }

  toDate(): string {
    return this.today.setLocale('en-US').toFormat('d-MMMM-yyyy')
  }

  calculateTimeDifference(unit: 'days' | 'months'): number {
    const diff = this.today.diff(this.displayDate, unit).toObject()[unit] as number;
    return Math.trunc(diff);
  }

  onDateChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    const newDisplayDate = DateTime.fromISO(value);
    if (!newDisplayDate.isValid) return;

    this.displayDate = newDisplayDate;
    console.log(this.displayDate.toISODate())
    this.getBidRoundInformation();
  }

  onBidCategoryChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const value = target.value as BidRoundType;
    this.currentBidCategory = value;
    this.getBidRoundInformation();
  }

  stringOrNA(value: string): string {
    return value !== DEFAULT_RESPONSE_ITEM_VALUE ? value : 'N.A.';
  }

  disableNext(): boolean {
    return this.calculateTimeDifference(this.isDayAhead() ? 'days' : 'months') === 0;
  }

  getBidRoundStatusClass(bidRound: BidRoundStatus) {
    if (!this.status) return 'status-container-disabled';
    return this.status === bidRound ? 'status-container' : 'status-container-disabled';
  }

  goToReport(filter: ReportFilterOptions): void {
    console.log("Going to report with value: ", filter)
    this.router.navigate(['/report'], { queryParams: { date: this.displayDate.toISO(), category: this.currentBidCategory, filter: filter } })
  }
}
